/**
 * The admin and reception application.
 *
 * The kiosk is a separate entry point (`kiosk.html` / `src/kiosk/`), so a
 * guest tablet never downloads any of this.
 */

import { lazy, Suspense, useEffect, type ReactNode } from 'react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { Link, matchPath, RouterProvider, Routes, useRouter } from '@/app/router'
import { landingPath } from '@/app/navigation'
import { Shell } from '@/app/Shell'
import { AuthProvider, useAuth } from '@/lib/auth'
import { ToastProvider } from '@/components/ui/toast'
import { LoadingState, NoAccessState } from '@/components/ui/states'
import { ChangePasswordPage } from '@/features/auth/ChangePasswordPage'
import { LoginPage } from '@/features/auth/LoginPage'
import { ResetPasswordPage } from '@/features/auth/ResetPasswordPage'
import { SelectPropertyPage } from '@/features/auth/SelectPropertyPage'
import { WorkspacePage } from '@/features/workspace/WorkspacePage'

// Everything past the critical path is split out, so the reception screen a
// receptionist opens forty times a day is not carrying the template editor.
const DashboardPage = lazy(() => import('@/features/dashboard/DashboardPage'))
const ArchivePage = lazy(() => import('@/features/archive/ArchivePage'))
const StayDetailPage = lazy(() => import('@/features/dashboard/StayDetailPage'))
const UsersPage = lazy(() => import('@/features/admin/UsersPage'))
const RolesPage = lazy(() => import('@/features/admin/RolesPage'))
const DesksPage = lazy(() => import('@/features/admin/DesksPage'))
const DevicesPage = lazy(() => import('@/features/admin/DevicesPage'))
const TemplatesPage = lazy(() => import('@/features/templates/TemplatesPage'))
const TemplateEditorPage = lazy(() => import('@/features/templates/TemplateEditorPage'))
const ReportsPage = lazy(() => import('@/features/admin/ReportsPage'))
const AuditPage = lazy(() => import('@/features/admin/AuditPage'))
const SettingsPage = lazy(() => import('@/features/admin/SettingsPage'))
const MigrationPage = lazy(() => import('@/features/admin/MigrationPage'))
const MediaPage = lazy(() => import('@/features/signage/MediaPage'))
const PlaylistsPage = lazy(() => import('@/features/signage/PlaylistsPage'))
const SchedulesPage = lazy(() => import('@/features/signage/SchedulesPage'))
const AccountPage = lazy(() => import('@/features/account/AccountPage'))
const HelpPage = lazy(() => import('@/features/help/HelpPage'))

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: (failureCount, error) => {
        // Never retry a permission or validation failure; the answer will not
        // change, and retrying just delays a useful error message.
        const status = (error as { status?: number })?.status
        if (status && status < 500 && status !== 429) return false
        return failureCount < 2
      },
      refetchOnWindowFocus: false,
    },
  },
})

interface AppRoute {
  path: string
  element: (params: Record<string, string>) => ReactNode
  /** The permission needed to render it. Checked again server-side. */
  permission?: string
}

/**
 * The route table, in sidebar order.
 *
 * `permission` is what makes a typed or bookmarked URL degrade into an
 * explanation rather than a 403 in a page shell, and the order is what
 * decides where each role lands after signing in — see `landingPath`.
 */
const ROUTES: AppRoute[] = [
  {
    path: '/workspace',
    element: () => <WorkspacePage />,
    permission: 'capture:read',
  },
  {
    path: '/dashboard',
    element: () => <DashboardPage />,
    permission: 'documents:read',
  },
  {
    path: '/stays/:stayId',
    element: (p: Record<string, string>) => <StayDetailPage stayId={p.stayId} />,
    permission: 'documents:read',
  },
  {
    path: '/archive',
    element: () => <ArchivePage />,
    permission: 'documents:read',
  },
  {
    path: '/signage/media',
    element: () => <MediaPage />,
    permission: 'signage:read',
  },
  {
    path: '/signage/playlists',
    element: () => <PlaylistsPage />,
    permission: 'signage:read',
  },
  {
    path: '/signage/schedules',
    element: () => <SchedulesPage />,
    permission: 'signage:read',
  },
  { path: '/admin/users', element: () => <UsersPage />, permission: 'users:read' },
  { path: '/admin/roles', element: () => <RolesPage />, permission: 'roles:read' },
  { path: '/admin/desks', element: () => <DesksPage />, permission: 'desks:read' },
  { path: '/admin/devices', element: () => <DevicesPage />, permission: 'devices:read' },
  {
    path: '/admin/templates',
    element: () => <TemplatesPage />,
    permission: 'templates:read',
  },
  {
    path: '/admin/templates/versions/:versionId',
    element: (p: Record<string, string>) => <TemplateEditorPage versionId={p.versionId} />,
    permission: 'templates:read',
  },
  {
    path: '/admin/reports',
    element: () => <ReportsPage />,
    permission: 'reports:read',
  },
  {
    path: '/admin/audit',
    element: () => <AuditPage />,
    permission: 'audit:read',
  },
  { path: '/admin/settings', element: () => <SettingsPage />, permission: 'settings:read' },
  // No longer in the rail (Help took its place); kept for a Super Admin who
  // needs the import report or the ITSthe1.ID checks by address.
  { path: '/admin/migration', element: () => <MigrationPage />, permission: 'system:admin' },
  // Every role: your own picture, and the printer for the PC you sit at.
  // Reached from your name at the foot of the rail, not from a section.
  { path: '/account', element: () => <AccountPage /> },
  // Every role, from the foot of the rail too.
  { path: '/help', element: () => <HelpPage /> },
]

function AuthenticatedApp() {
  const {
    session,
    loading,
    can,
    canAny,
    properties,
    propertiesLoaded,
    resolvingProperty,
    propertyError,
  } =
    useAuth()
  const { path, navigate } = useRouter()

  // Send "/" to the first screen this role can use. Done in an effect and as
  // a replace, so it does not put a redirect the user never chose into their
  // back history.
  const home = session ? landingPath(can, canAny) : '/workspace'
  useEffect(() => {
    if (session && path === '/') navigate(home, { replace: true })
  }, [session, path, home, navigate])

  if (path.startsWith('/reset-password')) return <ResetPasswordPage />

  if (loading) {
    return (
      <div className="mx-auto max-w-3xl px-6 py-16">
        <LoadingState rows={4} />
      </div>
    )
  }

  if (!session) return <LoginPage />

  // A freshly seeded or admin-reset account must replace its password before it
  // can reach anything else. The flag is authoritative on the server; this is
  // the client honouring it.
  if (session.user.must_change_pw) return <ChangePasswordPage />

  // A cross-property administrator has no property of their own, and every
  // scoped screen needs one. The single-property case is picked automatically
  // in the auth provider (show a skeleton while it resolves); a real choice, a
  // property that has yet to be created, or an automatic pick that *failed*,
  // goes to the chooser.
  //
  // That last case is why `propertyError` is in the condition: the skeleton
  // used to be shown for the whole single-property case on the assumption the
  // automatic pick was about to succeed, so when it did not, the screen said
  // "Loading" for ever — which is exactly what hid the retry loop that used to
  // sit behind it.
  const isSuperAdmin = session.permissions.includes('system:admin')
  if (isSuperAdmin && !session.property) {
    if (
      !propertyError &&
      (!propertiesLoaded || resolvingProperty || properties.length === 1)
    ) {
      return (
        <div className="mx-auto max-w-3xl px-6 py-16">
          <LoadingState rows={4} />
        </div>
      )
    }
    return <SelectPropertyPage />
  }

  // "/" is not a route of its own; the effect above is already redirecting it.
  // Rendering the skeleton for that one frame stops "that page does not exist"
  // flashing on every sign-in.
  if (path === '/') {
    return (
      <div className="mx-auto max-w-3xl px-6 py-16">
        <LoadingState rows={4} />
      </div>
    )
  }

  // Hide, rather than render-then-403, anything this role may not open.
  const allowed = ROUTES.filter((route) => !route.permission || can(route.permission))
  const known = ROUTES.some((route) => matchPath(route.path, path))

  return (
    <Shell>
      <Suspense fallback={<LoadingState rows={5} />}>
        <Routes
          routes={allowed}
          notFound={
            known ? (
              <NoAccessState
                home={
                  <Link
                    to={home}
                    className="text-[13px] font-medium text-[var(--brand)] hover:underline"
                  >
                    Go to your home screen
                  </Link>
                }
              />
            ) : (
              <div className="py-16 text-center">
                <h1 className="text-lg font-semibold">That page does not exist</h1>
                <p className="mt-1 text-[13px] text-[var(--text-muted)]">
                  Check the address, or pick something from the sidebar.
                </p>
              </div>
            )
          }
        />
      </Suspense>
    </Shell>
  )
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <RouterProvider>
        <ToastProvider>
          <AuthProvider>
            <AuthenticatedApp />
          </AuthProvider>
        </ToastProvider>
      </RouterProvider>
    </QueryClientProvider>
  )
}
